import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import { LinkedChecklistEntry } from '../interfaces/checklist.model';
import { CheckListService } from './check-list.service';
import { ChecklistHelperService } from './checklist.helper.service';
import { HttpRequestService } from '../shared/services/http-request.service';
import { NotificationService } from '../shared/services/notification.service';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { HCBS_CHECKLIST_ITEM_IDS, ON_DUTY_MEAL_BREAK_CHECKLIST_ITEM_ID } from '../shared/constants/static-data.constants';
import { formatUrl } from '../shared/utils/format-url-util';

@Injectable({
  providedIn: 'root'
})
export class ToDoService {
  constructor(
    private checkListService: CheckListService,
    private checklistHelperService: ChecklistHelperService,
    private httpRequestService: HttpRequestService,
    private notificationService: NotificationService
  ) { }

  // Service credentials are only relevant to Independent Facilitators (IF). Each responsedata entry
  // is a participant whose service credential is still pending.
  getServiceCredentialStatus(programCode: string, providerId: number | string): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.GET_SERVICE_CREDENTIAL_STATUS,
      { program_code: programCode },
      this.notificationService
    );
    return this.httpRequestService.post(url, { providerId: Number(providerId) }).pipe(
      catchError(() => of({ responsedata: [] }))
    );
  }

  /**
   * The linked Employer/Provider checklists for a provider, in both directions: the provider
   * acting as an Employer ("Checklist for Providers") and as an Independent Provider
   * ("Checklist for Employers"). Both list endpoints are called regardless of the provider's own
   * type — a direction that does not apply simply returns no rows — then each row's checklist is
   * fetched and grouped the same way participant association checklists are.
   */
  getLinkedChecklists(programCode: string, providerId: string): Observable<LinkedChecklistEntry[]> {
    return forkJoin([
      this.listRows(this.checkListService.getEmployerProviderList(programCode, providerId)),
      this.listRows(this.checkListService.getProviderEmployerList(programCode, providerId))
    ]).pipe(
      map(([employerProviderRows, providerEmployerRows]) => [
        ...employerProviderRows.map((row: any) => ({ row, mode: 'employerProvider' as const })),
        ...providerEmployerRows.map((row: any) => ({ row, mode: 'providerEmployer' as const }))
      ]),
      mergeMap(targets => targets.length === 0
        ? of([] as LinkedChecklistEntry[])
        : from(targets).pipe(
          // One checklist request per row, throttled for the same reason as the association calls.
          mergeMap(
            ({ row, mode }, index) => this.linkedChecklistRequest(programCode, providerId, row, mode).pipe(
              catchError(() => of(null)),
              map((response: any) => ({ row, mode, response, index }))
            ),
            3
          ),
          toArray(),
          map(pairs => pairs
            .sort((left, right) => left.index - right.index)
            .filter(({ response }) => response?.responsedata?.checkList)
            .map(({ row, mode, response }) => this.toLinkedChecklistEntry(row, mode, response))
          )
        )
      )
    );
  }

  /** Both list endpoints return either a bare array or a { list } wrapper. */
  private listRows(source: Observable<any>): Observable<any[]> {
    return source.pipe(
      catchError(() => of(null)),
      map((res: any) => Array.isArray(res?.responsedata) ? res.responsedata : (res?.responsedata?.list ?? []))
    );
  }

  private linkedChecklistRequest(
    programCode: string,
    providerId: string,
    row: any,
    mode: LinkedChecklistEntry['mode']
  ): Observable<any> {
    return mode === 'employerProvider'
      // The provider is the Employer here, so the row's provider is the counterparty.
      ? this.checkListService.getEmployerProviderChecklist(programCode, providerId, String(row.prvId), 'EMP')
      : this.checkListService.getProviderEmployerChecklist(programCode, providerId, String(row.empId), 'IR');
  }

  private toLinkedChecklistEntry(row: any, mode: LinkedChecklistEntry['mode'], response: any): LinkedChecklistEntry {
    const isEmployerProvider = mode === 'employerProvider';
    return {
      mode,
      counterpartyType: isEmployerProvider ? 'Provider' : 'Employer',
      counterpartyName: isEmployerProvider ? row.prvName : row.empName,
      counterpartyPplId: isEmployerProvider ? row.prvPplId : row.empPplId,
      checklistGroups: this.checklistHelperService.buildChecklistDataForProvider(
        response.responsedata.checkList,
        row,
        false,
        false
      )
    };
  }

  getAssociationChecklists(programCode: string, uniqueAssociations: any[]): Observable<any[]> {
    return forkJoin(
      uniqueAssociations.map((assoc: any) =>
        this.checkListService.getChecklistForParticipantAssociation(
          programCode,
          String(assoc.providerId),
          String(assoc.participantId),
          assoc.providerType
        ).pipe(
          catchError(() => of(null)),
          map((response: any) => ({ assoc, response }))
        )
      )
    ).pipe(
      map((pairs: any[]) =>
        pairs
          .filter(({ response }) => response?.responsedata?.checkList)
          .map(({ assoc, response }) => {
            const checkList = response.responsedata.checkList;
            // The On-Duty Meal Break form only applies to Independent Providers (IR/IP), identified
            // by its checklistItemId. Driven by formStatus and excluded from the category-based
            // checklist groups to avoid duplicate/inflated counts.
            const isIndependentProvider = assoc.providerType === 'IR' || assoc.providerType === 'IP';
            const onDutyItemId = isIndependentProvider ? ON_DUTY_MEAL_BREAK_CHECKLIST_ITEM_ID : undefined;
            const onDutyItem = onDutyItemId ? (checkList || []).find((e: any) => e.checklistItemId === onDutyItemId) : undefined;
            const onDutyMealBreakPending = !!onDutyItem && onDutyItem.formStatus === 'P';
            const onDutyMealBreakCompleted = !!onDutyItem && onDutyItem.formStatus === 'C';
            const onDutyMealBreakLabel = onDutyItem ? this.checklistHelperService.buildFormTodoLabel(onDutyItem.formDesc) : '';

            // The HCBS form only applies to Vendor (VE) and Independent Contractor (IC) associations,
            // identified by a provider-type-specific checklistItemId. Like the On-Duty form it is
            // driven by formStatus and excluded from the category-based groups to avoid duplicates.
            const hcbsItemId = HCBS_CHECKLIST_ITEM_IDS[assoc.providerType];
            const hcbsItem = hcbsItemId ? (checkList || []).find((e: any) => e.checklistItemId === hcbsItemId) : undefined;
            const hcbsFormPending = !!hcbsItem && hcbsItem.formStatus === 'P';
            const hcbsFormCompleted = !!hcbsItem && hcbsItem.formStatus === 'C';
            const hcbsFormLabel = hcbsItem ? this.checklistHelperService.buildFormTodoLabel(hcbsItem.formDesc) : '';

            const isExcludedFromGroups = (e: any) =>
              (!!onDutyItemId && e.checklistItemId === onDutyItemId)
              || (!!hcbsItemId && e.checklistItemId === hcbsItemId);

            const groupCheckList = (checkList || []).filter((e: any) => !isExcludedFromGroups(e));
            // The held-out items stay out of checklistGroups so they are counted once, but they are
            // still required checklist items — carried here so the association's To-Do entry can list
            // them alongside their siblings.
            const itemsExcludedFromGroups = (checkList || []).filter(isExcludedFromGroups);
            return {
              ...assoc,
              onDutyMealBreakPending,
              onDutyMealBreakCompleted,
              onDutyMealBreakLabel,
              hcbsFormPending,
              hcbsFormCompleted,
              hcbsFormLabel,
              itemsExcludedFromGroups,
              checklistGroups: this.checklistHelperService.buildChecklistDataForProvider(
                groupCheckList,
                assoc,
                false,
                false
              )
            };
          })
      )
    );
  }
}
